import { format } from 'date-fns';
import { stringify } from 'query-string';
import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router';

import { useAdminUsers } from '@dashboard/hooks/queries/useAdminQueries';
import { useDeleteUser, useDisableUserStores } from '@dashboard/hooks/queries/useAdminMutations';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { XMarkIcon } from '@heroicons/react/24/outline';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { FormElement, Label, TextInput } from '@dashboard/components/Form';
import Link from '@dashboard/components/Link';
import { Badge } from '@/dashboard/components/ui/badge';
import { useAuth } from '@/dashboard/context/AuthContext';
import { toast } from '@/dashboard/context/ToastContext';
import { confirm } from '@dashboard/lib/confirm';
import { prompt } from '@dashboard/lib/prompt';
import Button from '@dashboard/components/Button';
import { AdminRolesEnum } from '@/dashboard/enums/admin-roles';

const Users = () => {
  const history = useHistory();
  const urlQuery = useUrlQuery();
  const { user: adminUser } = useAuth();

  const { searchInput, setSearchInput, search } = useSearchInput(urlQuery.search);
  const [sortBy, setSortBy] = useState(urlQuery.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(urlQuery.sortDirection || -1);
  const [limit] = useState(20);

  const deleteUserMutation = useDeleteUser({
    onSuccess: () => {
      toast({
        title: 'User deleted',
      });
    },
    onError: error => {
      window.alert(error.message);
    },
  });

  const disableUserStoresMutation = useDisableUserStores({
    onSuccess: (_, { notify }) => {
      toast({
        title: 'User stores disabled',
        description: notify
          ? 'All stores have been disabled and the user has been notified via email.'
          : 'All stores have been disabled.',
      });
    },
    onError: error => {
      window.alert(error.message);
    },
  });

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        ...(search && { search }),
      }),
    });
  }, [sortBy, sortDirection, search]);

  const filters = {
    sortBy,
    sortDirection,
    search,
    limit,
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useAdminUsers(
    filters,
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      retryDelay: 1000,
      staleTime: 1000 * 60 * 60 * 24,
    },
  );

  const sorting = useMemo(
    () => [
      {
        id: sortBy,
        desc: sortDirection === -1,
      },
    ],
    [sortBy, sortDirection],
  );

  const handleSortingChange = updater => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      setSortBy(id);
      setSortDirection(desc ? -1 : 1);
    }
  };

  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  async function handleDeleteUser(userId) {
    const confirmed1 = await confirm(
      'Are you sure you want to delete this user? This is irreversible.',
    );

    if (!confirmed1) {
      return;
    }

    const confirmed2 = await confirm('Are you ABSOLUTELY sure you want to delete this user?');

    if (!confirmed2) {
      return;
    }

    deleteUserMutation.mutate({ userId });
  }

  async function handleDisableUserStores(userId) {
    const result = await prompt(
      'Are you sure you want to disable all stores this user owns? They will no longer be able to log in. Enter a reason:',
      {
        placeholder: 'Reason',
        checkbox: {
          label: 'Send email to user',
          name: 'notify',
          defaultChecked: false,
        },
      },
    );

    if (!result || !result.value) {
      return;
    }

    disableUserStoresMutation.mutate({ userId, reason: result.value, notify: result.notify });
  }

  const columns = useMemo(
    () => [
      {
        accessorKey: 'email',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Email" />,
        cell: ({ row }) => {
          const user = row.original;
          return <div className="max-w-xs">{user.email}</div>;
        },
        enableSorting: true,
      },
      {
        accessorKey: 'name',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Name" />,
        cell: ({ row }) => {
          const user = row.original;
          return <div>{user.name || '-'}</div>;
        },
        enableSorting: true,
      },
      {
        id: 'sellersCount',
        accessorFn: row => row.sellers?.length ?? 0,
        header: ({ column }) => <DataGridColumnHeader column={column} title="No. stores" />,
        cell: ({ row }) => {
          const user = row.original;
          return <div className="flex flex-wrap gap-1">{user.sellers?.length}</div>;
        },
        enableSorting: true,
      },
      {
        id: 'sellers',
        header: 'Store(s)',
        cell: ({ row }) => {
          const user = row.original;
          if (!user.sellers?.length) return <div>-</div>;
          return (
            <div className="flex flex-wrap gap-1">
              {user.sellers.map(seller => {
                const userRole = seller.users.find(u => user._id === u.user)?.role;
                return (
                  <Badge key={seller._id} variant="outline">
                    <Link to={`/admin/merchants/${seller._id}`}>
                      {seller.displayName || seller.name} - {userRole}
                    </Link>
                  </Badge>
                );
              })}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Created at" />,
        cell: ({ row }) => {
          const createdAt = row.getValue('createdAt');
          return createdAt ? format(new Date(createdAt), 'd MMM, yyyy, HH:mm') : '';
        },
        enableSorting: true,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const user = row.original;
          const hasEnabledStores = user.sellers?.some(seller => {
            return seller.disabled === false;
          });

          const ownerStores = user.sellers?.filter(seller => {
            const userRole = seller.users.find(u => user._id === u.user)?.role;
            return userRole === 'owner';
          });

          return adminUser.role === AdminRolesEnum.ADMIN ? (
            <div className="flex gap-x-2">
              {ownerStores?.length > 0 && hasEnabledStores && (
                <Button
                  onClick={() => handleDisableUserStores(user._id)}
                  appearance="light"
                  size="small"
                >
                  Disable stores
                </Button>
              )}
              <Button onClick={() => handleDeleteUser(user._id)} appearance="light" size="small">
                Delete
              </Button>
            </div>
          ) : null;
        },
        enableSorting: false,
      },
    ],
    [adminUser],
  );

  const filterControls = (
    <div className="flex items-end gap-x-2">
      <FormElement className="!mb-0">
        <Label htmlFor="search">Search</Label>
        <div className="w-64">
          <div className="relative flex items-center">
            <TextInput
              border
              name="search"
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search by email or name"
              value={searchInput}
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="flex items-center absolute right-0 top-0 bottom-0 p-1.5 m-px bg-white dark:bg-background text-gray-300 dark:text-muted-foreground hover:text-gray-400 dark:hover:text-muted-foreground transition-colors rounded"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </FormElement>
    </div>
  );

  return (
    <React.Fragment>
      <div className="flex justify-end items-end mb-4">{filterControls}</div>

      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No users found</EmptyState.Title>
          </EmptyState>
        }
      />
    </React.Fragment>
  );
};

export default Users;
